import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { tenXService } from '../../../../../services/TenXService';

interface CaptureImagesButtonProps {
  deviceName: string;
  // Externally disable (e.g. while the live view is still loading).
  disabled?: boolean;
}

// Client-side cooldown after a successful trigger so operators can't fire
// captures back-to-back. This is advisory UX only to give an idea how long
// the data take to get through the pipeliens.
const COOLDOWN_SECONDS = 25;

const CaptureImagesButton: React.FC<CaptureImagesButtonProps> = ({
  deviceName,
  disabled = false,
}) => {
  const [capturing, setCapturing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // Seconds left in the cooldown; 0 means the button is ready.
  const [cooldown, setCooldown] = useState<number>(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = () => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Start a 1s countdown that clears itself when it reaches zero.
  // Setup below to run {COOLDOWN_SECONDS} times
  const startCooldown = () => {
    clearTimer();
    setCooldown(COOLDOWN_SECONDS);
    intervalRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearTimer();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Reset to a clean state whenever the selected device changes, and tear the
  // timer down on unmount to avoid leaks / setState-after-unmount.
  useEffect(() => {
    setCapturing(false);
    setError(null);
    setCooldown(0);
    clearTimer();
    return () => clearTimer();
  }, [deviceName]);

  const handleCapture = async () => {
    if (capturing || cooldown > 0) return;

    setCapturing(true);
    setError(null);

    try {
      await tenXService.captureDeviceImages(deviceName);
      // Only start the cooldown once the trigger actually succeeded.
      startCooldown();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to trigger image capture';
      setError(message);
    } finally {
      setCapturing(false);
    }
  };

  const isDisabled = disabled || capturing || cooldown > 0;

  let label = 'Capture Images';
  if (capturing) label = 'Capturing...';
  else if (cooldown > 0) label = `Wait ${cooldown}s`;

  return (
    <Box sx={{ mt: 2 }}>
      <Button
        variant="contained"
        color="secondary"
        onClick={handleCapture}
        disabled={isDisabled}
      >
        {label}
      </Button>

      {cooldown > 0 && !error && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Capture triggered. New frames will appear once the device finishes
          uploading — May take some time for the data to clear ingestion.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default CaptureImagesButton;
